/**
 * Tabs, Breadcrumb and Avatar.
 *
 * Grouped as "things that tell you where you are or who you are".
 */

import { Tabs as RadixTabs } from 'radix-ui';
import type { ReactNode } from 'react';

import { initials } from '~/lib/format';
import { cx } from '~/lib/cx';
import { ChevronRight } from './icons';

import styles from './Navigation.module.css';

/* ====================================================================== *
 * Tabs
 * ====================================================================== */

export interface TabsProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Names what the tabs switch between, for assistive technology. */
  label: string;
  items: { value: string; label: ReactNode; count?: number | undefined }[];
  children?: ReactNode;
  className?: string;
}

export function Tabs({ value, onValueChange, label, items, children, className }: TabsProps) {
  return (
    <RadixTabs.Root
      value={value}
      onValueChange={onValueChange}
      className={cx(styles.tabs, className)}
    >
      <RadixTabs.List className={styles.tabList} aria-label={label}>
        {items.map((item) => (
          <RadixTabs.Trigger key={item.value} value={item.value} className={styles.tab}>
            <span>{item.label}</span>
            {item.count === undefined ? null : (
              <span className={styles.tabCount}>{item.count}</span>
            )}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>
      {children}
    </RadixTabs.Root>
  );
}

export function TabPanel({ value, children }: { value: string; children: ReactNode }) {
  return (
    <RadixTabs.Content value={value} className={styles.tabPanel}>
      {children}
    </RadixTabs.Content>
  );
}

/* ====================================================================== *
 * Breadcrumb
 * ====================================================================== */

export interface BreadcrumbItem {
  label: ReactNode;
  href?: string;
}

/**
 * The trail to the current page.
 *
 * The last item is rendered as text with `aria-current="page"` rather than as
 * a link, because a link to the page you are on is a link that does nothing
 * and a keyboard user has to tab past it.
 */
export function Breadcrumb({ items, className }: { items: BreadcrumbItem[]; className?: string }) {
  return (
    <nav aria-label="Breadcrumb" className={cx(styles.breadcrumb, className)}>
      <ol className={styles.breadcrumbList}>
        {items.map((item, index) => {
          const last = index === items.length - 1;
          return (
            <li key={index} className={styles.breadcrumbItem}>
              {last || !item.href ? (
                <span
                  className={styles.breadcrumbCurrent}
                  {...(last ? { 'aria-current': 'page' as const } : {})}
                >
                  {item.label}
                </span>
              ) : (
                <a href={item.href} className={styles.breadcrumbLink}>
                  {item.label}
                </a>
              )}
              {last ? null : <ChevronRight size={12} className={styles.breadcrumbSeparator} />}
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

/* ====================================================================== *
 * Avatar
 * ====================================================================== */

export interface AvatarProps {
  /** The person's name. Used for the initials and for the accessible name. */
  name: string;
  src?: string;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
}

/**
 * Initials, or an image when one exists.
 *
 * No `Avatar.Root` from Radix: that primitive exists to manage an image's
 * loading state, and this build has no avatar images to load — the `User`
 * schema has no avatar field. When one is added, this is where it goes.
 *
 * The element is `aria-hidden` and the name is rendered as text beside it
 * wherever this appears, so there is no second announcement of a name the user
 * has already heard.
 */
export function Avatar({ name, src, size = 'md', className }: AvatarProps) {
  return (
    <span className={cx(styles.avatar, styles[`avatar-${size}`], className)} aria-hidden="true">
      {src ? <img src={src} alt="" className={styles.avatarImage} /> : initials(name)}
    </span>
  );
}

/* ====================================================================== *
 * Keyboard hint
 * ====================================================================== */

/**
 * A key or chord, for a shortcut hint.
 *
 * `⌘` on Apple platforms, `Ctrl` elsewhere, decided from the platform rather
 * than hard-coded, because a Windows user who reads `⌘K` is being told a
 * shortcut that does not exist on their machine.
 */
export function Kbd({ children, className }: { children: ReactNode; className?: string }) {
  return <kbd className={cx(styles.kbd, className)}>{children}</kbd>;
}

export function modifierKey(): string {
  if (typeof navigator === 'undefined') return 'Ctrl';
  // `navigator.platform` is deprecated, and `userAgentData` is Chromium-only,
  // so the user-agent string is what is actually available everywhere. This is
  // a label on a keyboard hint: being wrong costs a confusing hint, not a
  // broken shortcut, because the handler accepts both Meta and Control.
  return /Mac|iPhone|iPad|iPod/.test(navigator.userAgent) ? '⌘' : 'Ctrl';
}
